// src/components/JobView/Tabs/InstallationTab.js
// --------------------------------------------------------------------------
// Módulo de Instalación — validación y ensamblaje en obra, optimizado para
// móvil. El instalador valida pieza por pieza (checkbox o escaneo QR), reporta
// problemas (reutiliza ReportFailureModal) y consulta los manuales/planos.
//
// Modelo de datos que escribe en el documento del job:
//   installationAddress: string
//   installationDate:    string
//   installationNotes:   string
//   installationModules: [{
//       moduleId, blockName, status('pending'|'in_progress'|'completed'),
//       manualPdfUrl,
//       pieces: [{ pieceId, pieceName, quantity, verified, verifiedAt, verifiedBy }]
//   }]
//
// Los módulos se derivan de los BLOQUES del job y sus piezas de las PLANCHAS
// (sheet.block === block.name), que son la fuente real de cantidades
// (qtyPerSheet * repetitions), igual que en PiecesInventoryTab.
// --------------------------------------------------------------------------
import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
    doc, updateDoc, collection, getDocs, writeBatch, serverTimestamp, arrayUnion,
} from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { Html5QrcodeScanner } from 'html5-qrcode';
import {
    MapPin, Calendar, FileText, Save, ChevronDown, ChevronUp, QrCode, X,
    Upload, Eye, Download, Check, AlertTriangle, HardHat, Package, Loader2,
    CheckCircle2,
} from 'lucide-react';
import ReportFailureModal from './Modals/ReportFailureModal';
import { buildTaskAssignment, buildAssignmentEmail, buildAssignedByStamp } from '../../../utils/taskAssignment';
import { productionResetFields } from '../../../lib/production';

// --- Helpers de derivación de módulos --------------------------------------

const slugify = (s) =>
    (s || '').toString().trim().toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '') || 'modulo';

// Agrega las piezas de todas las planchas de un bloque, sumando cantidades.
const aggregatePieces = (sheets, filterFn) => {
    const map = new Map();
    (sheets || []).filter(filterFn).forEach((sheet) => {
        const reps = Number(sheet.repetitions) || 1;
        (sheet.pieces || []).forEach((p) => {
            if (!p.code) return;
            const qty = (Number(p.qtyPerSheet) || 1) * reps;
            if (!map.has(p.code)) {
                map.set(p.code, {
                    pieceId: p.code,
                    pieceName: p.name || p.code,
                    quantity: 0,
                    verified: false,
                    verifiedAt: null,
                    verifiedBy: null,
                });
            }
            map.get(p.code).quantity += qty;
        });
    });
    return Array.from(map.values());
};

// Construye los módulos a partir de bloques + planchas. Si el job no tiene
// bloques, genera un único módulo "General" con todas las piezas.
const deriveModules = (jobData, sheets) => {
    const blocks = (jobData?.hasBlocks && Array.isArray(jobData.blocks))
        ? jobData.blocks.filter((b) => b && (b.name || b.code))
        : [];

    if (blocks.length > 0) {
        return blocks.map((b) => ({
            moduleId: slugify(b.code || b.name),
            blockName: b.name || b.code,
            status: 'pending',
            manualPdfUrl: '',
            pieces: aggregatePieces(sheets, (s) => s.block === b.name),
        }));
    }

    return [{
        moduleId: 'general',
        blockName: 'Módulo General',
        status: 'pending',
        manualPdfUrl: '',
        pieces: aggregatePieces(sheets, () => true),
    }];
};

// Estado del módulo en función de cuántas piezas están verificadas.
const computeStatus = (pieces = []) => {
    if (pieces.length === 0) return 'pending';
    const verified = pieces.filter((p) => p.verified).length;
    if (verified === 0) return 'pending';
    if (verified === pieces.length) return 'completed';
    return 'in_progress';
};

const STATUS_BADGE = {
    pending: { label: 'Pendiente', cls: 'bg-gray-100 text-gray-700' },
    in_progress: { label: 'En progreso', cls: 'bg-yellow-100 text-yellow-800' },
    completed: { label: 'Completado', cls: 'bg-green-100 text-green-800' },
};

// --- Escáner QR a pantalla completa ----------------------------------------
const QRScannerFullscreen = ({ onClose, onScan }) => {
    useEffect(() => {
        const scanner = new Html5QrcodeScanner(
            'install-qr-reader',
            { qrbox: { width: 250, height: 250 }, fps: 10 },
            false,
        );
        const handleSuccess = (decodedText) => {
            try { scanner.clear(); } catch (e) { /* noop */ }
            onScan(decodedText);
        };
        scanner.render(handleSuccess, () => {});
        return () => {
            if (scanner && scanner.getState() !== 3) {
                scanner.clear().catch(() => {});
            }
        };
    }, [onScan]);

    return (
        <div className="fixed inset-0 bg-black z-[70] flex flex-col">
            <div className="flex justify-between items-center p-4 text-white">
                <h2 className="text-lg font-bold flex items-center gap-2"><QrCode size={20} /> Escanear pieza</h2>
                <button
                    onClick={onClose}
                    className="min-h-[44px] min-w-[44px] flex items-center justify-center bg-white/10 rounded-full"
                    aria-label="Cerrar escáner"
                >
                    <X size={28} />
                </button>
            </div>
            <div className="flex-grow flex items-start justify-center overflow-y-auto">
                <div id="install-qr-reader" className="w-full max-w-md p-2" />
            </div>
        </div>
    );
};

// --- Card de pieza ----------------------------------------------------------
const PieceCard = ({ piece, onToggle, onReport }) => (
    <div className={`rounded-lg border p-3 ${piece.verified ? 'bg-green-50 border-green-300' : 'bg-white border-gray-200'}`}>
        <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
                <p className="font-semibold text-gray-800 truncate">{piece.pieceName}</p>
                <p className="text-xs text-gray-500 font-mono">{piece.pieceId}</p>
                <p className="text-xs text-gray-600 mt-1">Cantidad requerida: <span className="font-bold">{piece.quantity}</span></p>
            </div>
            <span className={`flex-shrink-0 px-2 py-1 rounded-full text-xs font-semibold ${piece.verified ? 'bg-green-200 text-green-800' : 'bg-gray-100 text-gray-500'}`}>
                {piece.verified ? '✓ Verificada' : '✗ Pendiente'}
            </span>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 mt-3">
            <button
                onClick={() => onToggle(!piece.verified)}
                className={`flex-1 min-h-[44px] rounded-md font-semibold flex items-center justify-center gap-2 ${piece.verified ? 'bg-gray-200 text-gray-700' : 'bg-green-600 text-white'}`}
            >
                <Check size={18} /> {piece.verified ? 'Desmarcar' : 'Verificar'}
            </button>
            <button
                onClick={onReport}
                className="flex-1 min-h-[44px] rounded-md font-semibold flex items-center justify-center gap-2 bg-red-100 text-red-700"
            >
                <AlertTriangle size={18} /> Reportar problema
            </button>
        </div>
    </div>
);

// --- Card de módulo (expandible) -------------------------------------------
const ModuleCard = ({
    module, expanded, onToggleExpand, onTogglePiece, onReportPiece,
    onScan, onUploadManual, uploading,
}) => {
    const badge = STATUS_BADGE[module.status] || STATUS_BADGE.pending;
    const fileInputRef = useRef(null);
    const verifiedCount = module.pieces.filter((p) => p.verified).length;

    return (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
            <button
                onClick={onToggleExpand}
                className="w-full min-h-[44px] flex items-center justify-between gap-3 p-4 text-left"
            >
                <div className="min-w-0">
                    <p className="font-bold text-gray-800 truncate flex items-center gap-2">
                        <Package size={18} className="text-indigo-500 flex-shrink-0" /> {module.blockName}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">{verifiedCount}/{module.pieces.length} piezas verificadas</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${badge.cls}`}>{badge.label}</span>
                    {expanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </div>
            </button>

            {expanded && (
                <div className="px-4 pb-4 space-y-4 border-t border-gray-100 pt-4">
                    {/* Acciones del módulo: manual + escanear */}
                    <div className="flex flex-col sm:flex-row gap-2">
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="application/pdf"
                            className="hidden"
                            onChange={(e) => {
                                const f = e.target.files?.[0];
                                if (f) onUploadManual(f);
                                e.target.value = '';
                            }}
                        />
                        {module.manualPdfUrl ? (
                            <a
                                href={module.manualPdfUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex-1 min-h-[44px] rounded-md font-semibold flex items-center justify-center gap-2 bg-blue-600 text-white"
                            >
                                <Eye size={18} /> Ver manual
                            </a>
                        ) : (
                            <button
                                onClick={() => fileInputRef.current?.click()}
                                disabled={uploading}
                                className="flex-1 min-h-[44px] rounded-md font-semibold flex items-center justify-center gap-2 bg-gray-100 text-gray-700 disabled:opacity-60"
                            >
                                {uploading ? <Loader2 size={18} className="animate-spin" /> : <Upload size={18} />}
                                {uploading ? 'Subiendo...' : 'Subir manual'}
                            </button>
                        )}
                        {module.manualPdfUrl && (
                            <button
                                onClick={() => fileInputRef.current?.click()}
                                disabled={uploading}
                                className="min-h-[44px] px-4 rounded-md font-semibold flex items-center justify-center gap-2 bg-gray-100 text-gray-700 disabled:opacity-60"
                            >
                                {uploading ? <Loader2 size={18} className="animate-spin" /> : <Upload size={18} />} Reemplazar
                            </button>
                        )}
                        <button
                            onClick={onScan}
                            disabled={module.pieces.length === 0}
                            className="flex-1 min-h-[44px] rounded-md font-semibold flex items-center justify-center gap-2 bg-indigo-600 text-white disabled:opacity-50"
                        >
                            <QrCode size={18} /> Escanear QR
                        </button>
                    </div>

                    {/* Lista de piezas (cards verticales, sin tablas) */}
                    {module.pieces.length > 0 ? (
                        <div className="space-y-2">
                            {module.pieces.map((piece) => (
                                <PieceCard
                                    key={piece.pieceId}
                                    piece={piece}
                                    onToggle={(v) => onTogglePiece(piece.pieceId, v)}
                                    onReport={() => onReportPiece(piece)}
                                />
                            ))}
                        </div>
                    ) : (
                        <p className="text-sm text-gray-500 text-center py-4">
                            Este módulo no tiene piezas en inventario todavía.
                        </p>
                    )}
                </div>
            )}
        </div>
    );
};

// --- Componente principal ---------------------------------------------------
const InstallationTab = ({ db, storage, jobData, sheets = [], user }) => {
    // Header editable.
    const [address, setAddress] = useState('');
    const [date, setDate] = useState('');
    const [notes, setNotes] = useState('');
    const [savingHeader, setSavingHeader] = useState(false);
    const [headerSaved, setHeaderSaved] = useState(false);

    // UI de módulos.
    const [expanded, setExpanded] = useState({});
    const [uploadingModuleId, setUploadingModuleId] = useState(null);
    const [scanModuleId, setScanModuleId] = useState(null);

    // Reporte de problema.
    const [reportContext, setReportContext] = useState(null); // { piece, module }
    const [allUsers, setAllUsers] = useState([]);

    const initRef = useRef(false);

    // Sincroniza el header con el job cuando carga / cambia.
    useEffect(() => {
        if (!jobData) return;
        setAddress(jobData.installationAddress || '');
        setDate(jobData.installationDate || '');
        setNotes(jobData.installationNotes || '');
    }, [jobData?.installationAddress, jobData?.installationDate, jobData?.installationNotes]);

    // Carga TODOS los usuarios para poder asignar la alerta a cualquiera.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const snap = await getDocs(collection(db, 'users'));
                if (!cancelled) setAllUsers(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
            } catch (e) {
                console.error('Error cargando usuarios para asignación:', e);
            }
        })();
        return () => { cancelled = true; };
    }, [db]);

    // Módulos: los persistidos mandan; si no existen, se derivan en memoria
    // (así la UI nunca está vacía) y se persisten en el efecto de abajo.
    const modules = useMemo(() => {
        if (Array.isArray(jobData?.installationModules)) return jobData.installationModules;
        return deriveModules(jobData || {}, sheets);
    }, [jobData?.installationModules, jobData, sheets]);

    // Inicialización: si el job aún no tiene installationModules y ya hay
    // planchas cargadas, deriva y persiste una sola vez.
    useEffect(() => {
        if (!jobData?.id || initRef.current) return;
        if (Array.isArray(jobData.installationModules)) return;
        if (!sheets || sheets.length === 0) return; // espera a que carguen las planchas
        initRef.current = true;
        const derived = deriveModules(jobData, sheets);
        updateDoc(doc(db, 'jobs', jobData.id), { installationModules: derived })
            .catch((e) => {
                console.error('No se pudieron inicializar los módulos de instalación:', e);
                initRef.current = false;
            });
    }, [db, jobData, sheets]);

    const persistModules = async (newModules) => {
        try {
            await updateDoc(doc(db, 'jobs', jobData.id), { installationModules: newModules });
        } catch (e) {
            console.error('Error guardando módulos de instalación:', e);
            alert('No se pudo guardar el cambio. Revisa tu conexión.');
        }
    };

    const handleSaveHeader = async () => {
        setSavingHeader(true);
        try {
            await updateDoc(doc(db, 'jobs', jobData.id), {
                installationAddress: address.trim(),
                installationDate: date,
                installationNotes: notes.trim(),
            });
            setHeaderSaved(true);
            setTimeout(() => setHeaderSaved(false), 2000);
        } catch (e) {
            console.error('Error guardando datos de instalación:', e);
            alert('No se pudieron guardar los datos de instalación.');
        } finally {
            setSavingHeader(false);
        }
    };

    const handleTogglePiece = async (moduleId, pieceId, verified) => {
        const newModules = modules.map((m) => {
            if (m.moduleId !== moduleId) return m;
            const pieces = m.pieces.map((p) => (
                p.pieceId === pieceId
                    ? {
                        ...p,
                        verified,
                        verifiedAt: verified ? new Date() : null,
                        verifiedBy: verified ? (user?.uid || user?.email || null) : null,
                    }
                    : p
            ));
            return { ...m, pieces, status: computeStatus(pieces) };
        });
        await persistModules(newModules);
    };

    const handleUploadManual = async (moduleId, file) => {
        if (!file) return;
        setUploadingModuleId(moduleId);
        try {
            const path = `jobs/${jobData.id}/installation/${moduleId}/manual.pdf`;
            const storageRef = ref(storage, path);
            const uploadTask = uploadBytesResumable(storageRef, file);
            await new Promise((resolve, reject) => {
                uploadTask.on('state_changed', () => {}, reject, resolve);
            });
            const url = await getDownloadURL(uploadTask.snapshot.ref);
            const newModules = modules.map((m) => (m.moduleId === moduleId ? { ...m, manualPdfUrl: url } : m));
            await persistModules(newModules);
        } catch (e) {
            console.error('Error subiendo el manual:', e);
            alert('No se pudo subir el manual del módulo.');
        } finally {
            setUploadingModuleId(null);
        }
    };

    // Resultado del escaneo: compara contra los pieceId del módulo activo.
    const handleScanResult = (decodedText) => {
        let code = decodedText;
        try {
            const parsed = JSON.parse(decodedText);
            if (parsed && parsed.pieceCode) code = parsed.pieceCode;
        } catch (e) { /* texto plano */ }

        const mod = modules.find((m) => m.moduleId === scanModuleId);
        const piece = mod?.pieces.find((p) => p.pieceId === code);
        setScanModuleId(null);

        if (!piece) {
            alert(`El código "${code}" no corresponde a ninguna pieza de este módulo.`);
            return;
        }
        if (piece.verified) {
            alert(`La pieza ${piece.pieceName} ya estaba verificada.`);
            return;
        }
        handleTogglePiece(mod.moduleId, piece.pieceId, true);
    };

    // Encuentra una plancha que contenga la pieza, para enlazar la alerta
    // con el flujo de Prepress (sheetId / estado de plancha).
    const findSheetForPiece = (pieceId) =>
        sheets.find((s) => (s.pieces || []).some((p) => p.code === pieceId));

    // Crea la tarea de sistema, escribe el correo y (si aplica) cambia el
    // estado de la plancha. Mismo flujo que PrepressTab.handleReportFailure.
    const handleReportSubmit = async ({
        failureType, failedCount, reason, assignedUser, aiCleanedDescription, aiSuggestedAction,
    }) => {
        if (!reportContext) return;
        const { piece, module } = reportContext;
        const finalDescription = aiCleanedDescription || reason;
        const isDesign = failureType === 'design';
        const batch = writeBatch(db);
        const linkedSheet = findSheetForPiece(piece.pieceId);

        const baseTask = {
            jobId: jobData.id,
            status: 'pending',
            createdBy: user.email,
            createdByName: user.displayName || user.email,
            createdAt: serverTimestamp(),
            originalDescription: reason,
            aiSuggestedAction: aiSuggestedAction || null,
            source: 'installation',
            pieceId: piece.pieceId,
            moduleId: module.moduleId,
            ...buildTaskAssignment(assignedUser),
            ...buildAssignedByStamp(user),
        };
        if (linkedSheet) {
            baseTask.sheetId = linkedSheet.id;
            baseTask.relatedPlateId = linkedSheet.id;
        }

        let taskData;
        if (isDesign) {
            taskData = {
                ...baseTask,
                type: 'system',
                title: `Corrección (Instalación): ${piece.pieceName}`,
                description: finalDescription,
            };
            if (linkedSheet) {
                batch.update(doc(db, 'jobs', jobData.id, 'sheets', linkedSheet.id), {
                    status: 'Devuelto a Diseño',
                    hasActiveAlert: true,
                    history: arrayUnion({
                        action: 'Devuelto a Diseño desde Instalación',
                        details: `Motivo: ${finalDescription}`,
                        user: user.email,
                        timestamp: new Date(),
                    }),
                });
            }
        } else {
            taskData = {
                ...baseTask,
                type: 'system_reproduction',
                title: `Reproducción (Instalación): ${failedCount}x ${piece.pieceName}`,
                description: `Se solicitaron ${failedCount} pieza(s) de reposición. Causa: ${finalDescription}`,
            };
            if (linkedSheet) {
                // La tarea lleva `sheetId` (ver arriba), así que esta plancha pasa a "Reproducir"
                // igual que desde Prepress: mismo reinicio de contadores, misma razón.
                batch.update(doc(db, 'jobs', jobData.id, 'sheets', linkedSheet.id), {
                    ...productionResetFields(),
                    hasActiveAlert: true,
                    history: arrayUnion({
                        action: 'Falla reportada desde Instalación',
                        details: `${failedCount} pieza(s). Razón: ${finalDescription}`,
                        user: user.email,
                        timestamp: new Date(),
                    }),
                });
            }
        }

        batch.set(doc(collection(db, 'jobs', jobData.id, 'tasks')), taskData);

        const mail = buildAssignmentEmail({
            assignedUser,
            assignedBy: user,
            taskTitle: taskData.title,
            taskDescription: taskData.description,
            jobId: jobData.id,
            suggestedAction: aiSuggestedAction,
        });
        if (mail) batch.set(doc(collection(db, 'mail')), mail);

        try {
            await batch.commit();
        } catch (e) {
            console.error('Error reportando problema de instalación:', e);
            alert('No se pudo crear la tarea del reporte.');
        }
    };

    // PDFs de referencia (Sección 3): por type explícito o, como respaldo,
    // por palabra clave en el nombre/descripción (los docs no siempre traen type).
    const referenceDocs = useMemo(() => {
        const TYPES = ['installation', 'plano', 'guia', 'manual'];
        const KEYWORD = /plano|gu[ií]a|manual|instalaci/i;
        return (jobData?.documents || []).filter((d) => {
            if (!d?.url) return false;
            const t = (d.type || '').toLowerCase();
            if (TYPES.includes(t)) return true;
            return KEYWORD.test(`${d.name || ''} ${d.description || ''}`);
        });
    }, [jobData?.documents]);

    if (!jobData) {
        return <div className="p-4 text-center text-gray-500">Cargando datos de instalación...</div>;
    }

    const reportPiece = reportContext?.piece;
    const reportModule = reportContext?.module;
    const reportInitialReason = reportPiece
        ? `Pieza ${reportPiece.pieceName} (código: ${reportPiece.pieceId}) del módulo ${reportModule.blockName} no disponible o dañada en obra`
        : '';

    return (
        <div className="max-w-3xl mx-auto space-y-4">
            {/* Escáner QR a pantalla completa */}
            {scanModuleId && (
                <QRScannerFullscreen
                    onClose={() => setScanModuleId(null)}
                    onScan={handleScanResult}
                />
            )}

            {/* Reporte de problema (reutiliza el modal compartido) */}
            <ReportFailureModal
                isOpen={!!reportContext}
                onClose={() => setReportContext(null)}
                onSubmit={handleReportSubmit}
                sheetName={reportPiece ? reportPiece.pieceName : ''}
                users={allUsers}
                initialFailureType="production"
                initialReason={reportInitialReason}
                sheetContext={reportModule ? `Módulo: ${reportModule.blockName} | Pieza: ${reportPiece.pieceId}` : ''}
            />

            {/* SECCIÓN 1 — Header de instalación */}
            <section className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
                <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2 mb-4">
                    <HardHat size={20} className="text-indigo-600" /> Datos de Instalación
                </h2>
                <div className="space-y-3">
                    <div>
                        <label className="text-xs font-semibold text-gray-600 flex items-center gap-1 mb-1"><MapPin size={14} /> Dirección</label>
                        <input
                            type="text"
                            value={address}
                            onChange={(e) => setAddress(e.target.value)}
                            placeholder="Dirección de la obra"
                            className="w-full min-h-[44px] px-3 border rounded-md"
                        />
                    </div>
                    <div>
                        <label className="text-xs font-semibold text-gray-600 flex items-center gap-1 mb-1"><Calendar size={14} /> Fecha de instalación</label>
                        <input
                            type="date"
                            value={date}
                            onChange={(e) => setDate(e.target.value)}
                            className="w-full min-h-[44px] px-3 border rounded-md"
                        />
                    </div>
                    <div>
                        <label className="text-xs font-semibold text-gray-600 flex items-center gap-1 mb-1"><FileText size={14} /> Notas generales</label>
                        <textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            rows="2"
                            placeholder="Notas para el equipo de instalación..."
                            className="w-full px-3 py-2 border rounded-md"
                        />
                    </div>
                    <button
                        onClick={handleSaveHeader}
                        disabled={savingHeader}
                        className={`w-full min-h-[44px] rounded-md font-bold text-white flex items-center justify-center gap-2 transition-colors ${headerSaved ? 'bg-green-500' : 'bg-blue-600 hover:bg-blue-700'} disabled:bg-gray-400`}
                    >
                        {savingHeader
                            ? <><Loader2 size={18} className="animate-spin" /> Guardando...</>
                            : headerSaved
                                ? <><CheckCircle2 size={18} /> ¡Guardado!</>
                                : <><Save size={18} /> Guardar datos</>}
                    </button>
                </div>
            </section>

            {/* SECCIÓN 2 — Módulos */}
            <section className="space-y-3">
                <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2 px-1">
                    <Package size={20} className="text-indigo-600" /> Módulos ({modules.length})
                </h2>
                {modules.length === 0 ? (
                    <p className="text-sm text-gray-500 bg-white border rounded-lg p-4 text-center">
                        Este trabajo no tiene bloques ni piezas para instalar.
                    </p>
                ) : (
                    modules.map((module) => (
                        <ModuleCard
                            key={module.moduleId}
                            module={module}
                            expanded={!!expanded[module.moduleId]}
                            onToggleExpand={() => setExpanded((prev) => ({ ...prev, [module.moduleId]: !prev[module.moduleId] }))}
                            onTogglePiece={(pieceId, v) => handleTogglePiece(module.moduleId, pieceId, v)}
                            onReportPiece={(piece) => setReportContext({ piece, module })}
                            onScan={() => setScanModuleId(module.moduleId)}
                            onUploadManual={(file) => handleUploadManual(module.moduleId, file)}
                            uploading={uploadingModuleId === module.moduleId}
                        />
                    ))
                )}
            </section>

            {/* SECCIÓN 3 — PDFs de referencia */}
            <section className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
                <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2 mb-4">
                    <FileText size={20} className="text-indigo-600" /> Manuales y planos
                </h2>
                {referenceDocs.length > 0 ? (
                    <div className="space-y-2">
                        {referenceDocs.map((d, i) => (
                            <div key={`${d.url}-${i}`} className="flex items-center justify-between gap-3 border rounded-md p-3">
                                <p className="text-sm text-gray-800 truncate min-w-0">{d.name || d.description || 'Documento'}</p>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                    <a
                                        href={d.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="min-h-[44px] min-w-[44px] flex items-center justify-center bg-blue-100 text-blue-700 rounded-md"
                                        title="Vista previa"
                                    >
                                        <Eye size={18} />
                                    </a>
                                    <a
                                        href={d.url}
                                        download
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="min-h-[44px] min-w-[44px] flex items-center justify-center bg-gray-100 text-gray-700 rounded-md"
                                        title="Descargar"
                                    >
                                        <Download size={18} />
                                    </a>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="text-sm text-gray-500 text-center py-4">No hay manuales cargados aún</p>
                )}
            </section>
        </div>
    );
};

export default InstallationTab;
